import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { ExternalLink, Loader2 } from "lucide-react";

export const affiliateLabel = (p) => {
  if (p.source === "awin") return "Awin deep link";
  if (p.affiliate_url && p.affiliate_active && p.affiliate_provider && p.affiliate_provider !== "none")
    return `Affiliate enabled (${p.affiliate_provider === "sovrn" ? "Sovrn" : "Direct"})`;
  if (p.affiliate_url) return "Affiliate link paused";
  return "No affiliate link";
};

export const AffiliateLinkEditor = ({ product, onSaved }) => {
  const [url, setUrl] = useState("");
  const [provider, setProvider] = useState("none");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    setUrl(product.affiliate_url || "");
    setProvider(product.affiliate_provider && product.affiliate_provider !== "awin" ? product.affiliate_provider : "none");
    setActive(product.affiliate_active !== false);
    setError(""); setStatus("");
  }, [product.product_id]);

  const isAwin = product.source === "awin";

  const save = async (override) => {
    setSaving(true); setError(""); setStatus("");
    const body = { affiliate_url: url.trim(), affiliate_provider: provider, affiliate_active: active, ...override };
    try {
      const r = await api.put(`/admin/products/${product.product_id}/affiliate`, body);
      onSaved?.(r.data);
      setUrl(r.data.affiliate_url || "");
      setProvider(r.data.affiliate_provider || "none");
      setStatus(body.affiliate_url ? "Affiliate link saved." : "Affiliate link removed.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save that link.");
    } finally { setSaving(false); }
  };

  return (
    <div className="col-span-2 rounded-xl border border-brand-cream2 p-4" data-testid="affiliate-link-editor">
      <p className="text-sm font-medium mb-1">Affiliate link</p>
      <p className="text-xs text-muted-foreground mb-3" data-testid="affiliate-link-status">
        {affiliateLabel(product)}
        {isAwin && " — Awin's own deep link is used for this product, so a Sovrn link isn't needed."}
      </p>
      <Label className="text-xs mb-1 block">Retailer URL (reference)</Label>
      <p className="text-xs text-muted-foreground break-all mb-3" data-testid="affiliate-retailer-url">{product.url || "—"}</p>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <Label className="text-xs mb-1 block">Affiliate URL</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://sovrn.co?key=…"
            data-testid="affiliate-url-input" />
        </div>
        <div>
          <Label className="text-xs mb-1 block">Provider</Label>
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger data-testid="affiliate-provider-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="sovrn">Sovrn</SelectItem>
              <SelectItem value="direct">Direct</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4">
        <div className="flex items-center gap-2">
          <Switch checked={active} onCheckedChange={setActive} data-testid="affiliate-active-toggle" />
          <span className="text-xs text-muted-foreground">Link live</span>
        </div>
        <Button onClick={() => save()} disabled={saving} data-testid="affiliate-save-button"
          className="bg-brand-green hover:bg-brand-greendark rounded-full h-9 gap-2">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save link
        </Button>
        <Button variant="outline" disabled={!url.trim()} data-testid="affiliate-test-button"
          onClick={() => window.open(url.trim(), "_blank", "noopener")}
          className="rounded-full h-9 gap-2"><ExternalLink className="w-3.5 h-3.5" /> Test link</Button>
        {product.affiliate_url && (
          <Button variant="ghost" disabled={saving} data-testid="affiliate-remove-button"
            onClick={() => { setUrl(""); setProvider("none"); save({ affiliate_url: "", affiliate_provider: "none" }); }}
            className="rounded-full h-9 text-brand-burgundy">Remove</Button>
        )}
      </div>
      {error && <p className="mt-3 text-sm text-brand-burgundy" data-testid="affiliate-link-error">{error}</p>}
      {status && !error && <p className="mt-3 text-sm text-brand-green" data-testid="affiliate-link-saved">{status}</p>}
    </div>
  );
};
